<template>
  <div v-loading="loading" class="nx-page nx-report-page kanban-device-fault-analysis">
    <nx-report-header
      title="设备故障统计分析"
    >
      <div class="kb-header-actions">
        <el-button size="small" icon="el-icon-refresh" @click="loadData">刷新</el-button>
      </div>
    </nx-report-header>

    <div class="kb-kpi-row">
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">筛选集条数</div>
        <div class="kb-kpi-value">{{ kpi.total }}</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">已关闭占比</div>
        <div class="kb-kpi-value">{{ kpi.closeRate }}%</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">涉及报警类型数</div>
        <div class="kb-kpi-value">{{ kpi.typeCount }}</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">涉及设备分类数</div>
        <div class="kb-kpi-value">{{ kpi.catCount }}</div>
      </div>
    </div>

    <div class="kb-grid-2">
      <div class="kb-chart-card">
        <div class="kb-chart-head">
          <span>报警类型分布</span>
        </div>
        <div ref="chartPie" class="kb-chart-box" />
      </div>
      <div class="kb-chart-card">
        <div class="kb-chart-head">
          <span>设备分类统计</span>
        </div>
        <div ref="chartBarCat" class="kb-chart-box" />
      </div>
    </div>

    <div class="kb-grid-2">
      <div class="kb-chart-card">
        <div class="kb-chart-head">
          <span>提醒级别对比</span>
        </div>
        <div ref="chartBarLevel" class="kb-chart-box" />
      </div>
      <div class="kb-chart-card">
        <div class="kb-chart-head">
          <span>高频设备 TOP10</span>
        </div>
        <div ref="chartBarDev" class="kb-chart-box" />
      </div>
    </div>

    <div class="kb-chart-card kb-chart-card--wide">
      <div class="kb-chart-head">
        <span>近 30 日报警趋势</span>
      </div>
      <div ref="chartLine" class="kb-chart-box kb-chart-box--tall" />
    </div>

  </div>
</template>

<script>
import moment from 'moment'
import * as echarts from 'echarts'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { fetchDeviceFaultAnalysis } from './api'
import { TYPES, CATEGORIES, LEVELS } from '../utils/mockDeviceAlarms'

export default {
  name: 'KanbanDeviceFaultAnalysis',
  components: { NxReportHeader },
  data() {
    return {
      loading: false,
      rawList: [],
      drill: { type: null, category: null },
      kpi: { total: 0, closeRate: '0', typeCount: 0, catCount: 0 },
      chartInstances: []
    }
  },
  computed: {
    filteredList() {
      return this.rawList.filter((r) => {
        if (this.drill.type && r.type !== this.drill.type) return false
        if (this.drill.category && r.category !== this.drill.category) return false
        return true
      })
    }
  },
  watch: {
    filteredList() {
      this.buildKpi()
      this.$nextTick(() => this.renderCharts())
    }
  },
  mounted() {
    this.loadData()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    this.disposeCharts()
  },
  methods: {
    goBoard() {
      this.$router.push('/kanban-management/device-alarm-board')
    },
    clearDrill() {
      this.drill = { type: null, category: null }
    },
    async loadData() {
      this.loading = true
      try {
        const res = await fetchDeviceFaultAnalysis({})
        this.rawList = (res && res.list) || []
        this.buildKpi()
        this.$nextTick(() => this.renderCharts())
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    buildKpi() {
      const data = this.filteredList
      const total = data.length
      const closed = data.filter((r) => r.alertStatus === 4).length
      const closeRate = total ? ((closed / total) * 100).toFixed(1) : '0'
      const typeCount = new Set(data.map((r) => r.type)).size
      const catCount = new Set(data.map((r) => r.category)).size
      this.kpi = { total, closeRate, typeCount, catCount }
    },
    disposeCharts() {
      this.chartInstances.forEach((c) => c && c.dispose())
      this.chartInstances = []
    },
    onResize() {
      this.chartInstances.forEach((c) => c && c.resize())
    },
    aggregateBy(arr, keyFn) {
      const m = {}
      arr.forEach((r) => {
        const k = keyFn(r)
        m[k] = (m[k] || 0) + 1
      })
      return m
    },
    renderCharts() {
      this.disposeCharts()
      const data = this.filteredList

      const byType = this.aggregateBy(data, (r) => r.type)
      const pieData = TYPES.map((name) => ({ name, value: byType[name] || 0 })).filter((x) => x.value > 0)
      const pie = echarts.init(this.$refs.chartPie)
      pie.setOption({
        tooltip: { trigger: 'item' },
        series: [
          {
            type: 'pie',
            radius: ['40%', '70%'],
            data: pieData.length ? pieData : [{ name: '无数据', value: 1, itemStyle: { color: '#e2e8f0' } }],
            emphasis: { scale: true },
            label: { formatter: '{b}\n{c} ({d}%)', fontSize: 11 }
          }
        ],
        color: ['#dc2626', '#ea580c', '#7c3aed', '#64748b']
      })
      pie.off('click')
      pie.on('click', (params) => {
        if (params.name === '无数据') return
        this.$router.push('/system/police-record/index')
      })
      this.chartInstances.push(pie)

      const byCat = this.aggregateBy(data, (r) => r.category)
      const catNames = CATEGORIES.filter((c) => byCat[c])
      const catVals = catNames.map((c) => byCat[c])
      const barCat = echarts.init(this.$refs.chartBarCat)
      barCat.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: '12%', right: '8%', bottom: '12%', top: '14%' },
        xAxis: { type: 'category', data: catNames.length ? catNames : CATEGORIES, axisLabel: { color: '#64748b' } },
        yAxis: { type: 'value', minInterval: 1, axisLabel: { color: '#64748b' } },
        series: [
          {
            type: 'bar',
            data: catNames.length ? catVals : [0, 0, 0],
            itemStyle: {
              color: (p) => {
                const colors = { 机电: '#3b82f6', 电气: '#8b5cf6', 配件: '#10b981' }
                return colors[p.name] || '#94a3b8'
              }
            },
            barMaxWidth: 48
          }
        ]
      })
      barCat.off('click')
      barCat.on('click', (params) => {
        const name = params.name
        if (!name || !CATEGORIES.includes(name)) return
        if (this.drill.category === name) this.drill.category = null
        else this.drill.category = name
      })
      this.chartInstances.push(barCat)

      const byLv = this.aggregateBy(data, (r) => r.alertType)
      const lvData = LEVELS.map((l) => byLv[l] || 0)
      const barLv = echarts.init(this.$refs.chartBarLevel)
      barLv.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: '10%', right: '8%', bottom: '10%', top: '12%' },
        xAxis: { type: 'value', minInterval: 1, axisLabel: { color: '#64748b' } },
        yAxis: { type: 'category', data: LEVELS, axisLabel: { color: '#64748b' } },
        series: [
          {
            type: 'bar',
            data: lvData.map((v, i) => ({
              value: v,
              itemStyle: { color: ['#b91c1c', '#ea580c', '#15803d'][i] }
            })),
            barMaxWidth: 22
          }
        ]
      })
      this.chartInstances.push(barLv)

      const byDev = this.aggregateBy(data, (r) => r.deviceName)
      const top = Object.entries(byDev)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
      const barDev = echarts.init(this.$refs.chartBarDev)
      barDev.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: '22%', right: '12%', bottom: '8%', top: '8%' },
        xAxis: { type: 'value', minInterval: 1, axisLabel: { color: '#64748b' } },
        yAxis: {
          type: 'category',
          data: top.map((x) => x[0]),
          inverse: true,
          axisLabel: { color: '#64748b', fontSize: 11 }
        },
        series: [{ type: 'bar', data: top.map((x) => x[1]), itemStyle: { color: '#6366f1' }, barMaxWidth: 18 }]
      })
      this.chartInstances.push(barDev)

      const keys = []
      for (let i = 29; i >= 0; i--) {
        keys.push(moment().startOf('day').subtract(i, 'day').format('YYYY-MM-DD'))
      }
      const dayCounts = keys.map((key) =>
        data.filter((r) => moment(r.alertTime).format('YYYY-MM-DD') === key).length
      )
      const line = echarts.init(this.$refs.chartLine)
      line.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: '3%', right: '4%', bottom: '14%', top: '8%', containLabel: true },
        xAxis: { type: 'category', data: keys.map((k) => k.slice(5)), axisLabel: { color: '#64748b', rotate: 45 } },
        yAxis: { type: 'value', minInterval: 1, axisLabel: { color: '#64748b' } },
        dataZoom: [{ type: 'inside', start: 0, end: 100 }],
        series: [
          {
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            data: dayCounts,
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(99,102,241,0.35)' },
                { offset: 1, color: 'rgba(99,102,241,0.03)' }
              ])
            },
            lineStyle: { color: '#6366f1', width: 2 },
            itemStyle: { color: '#6366f1' }
          }
        ]
      })
      this.chartInstances.push(line)
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';

.kb-header-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.kb-linkage-alert {
  margin-bottom: 14px;
  border-radius: 12px;
}
</style>
